import { useEffect, useState } from 'react'
import { SubmitHandler } from 'react-hook-form'
import { toast } from 'react-toastify'
import { useRouter } from 'next/router'
import { useUnmount } from 'react-use'
import { NextSeo } from 'next-seo'
import axios from 'axios'

import { routes } from 'utils/routes'
import { Form, useForm } from 'components/form'
import {
  CheckboxFormField,
  CURRENT_PASSWORD_RULE,
  EMAIL_RULE,
  EmailFormField,
} from 'components/form-field'
import { FormNameType } from 'utils/types'
import { PasswordFormField } from 'components/form-field/TextFormField/PasswordFormField'
import { SubmitFormButton } from 'components/submit-form-button'
import { Link } from 'components/links'
import { yup } from 'components/form/yup'
import { cn } from 'utils'
import { nest } from 'api'
import { useCallbackOnce } from 'utils/use-callback-once'
import { parseJwt } from 'utils/parse-jwt'
import { dateFormatter, unixToDate } from 'utils/date'
import {
  AutHeader,
  AuthPartnerPortalLayout,
  RegistrationLink,
} from 'components/layout/auth-partner-portal-layout'
import { VerifyEmailDialog } from 'components/verify-email-dialog'
import { verificationForEnum } from 'components/verify-email-dialog/types'

import classes from './login.module.css'

interface IFormModel {
  email: string
  password: string
  remember: boolean
}

const FORM_FIELD_NAME: FormNameType<IFormModel> = {
  email: 'email',
  password: 'password',
  remember: 'remember',
}

const VALIDATOR = yup.object().shape({
  [FORM_FIELD_NAME.email]: EMAIL_RULE.trim().required(),
  [FORM_FIELD_NAME.password]: CURRENT_PASSWORD_RULE.required(),
})

const REDIRECT_LOGIN_TOAST_ID = 'redirect-login-toast'

export default function Login() {
  const [showVerifyEmailDialog, setShowVerifyEmailDialog] = useState(false)
  const [brokerUuid, setBrokerUuid] = useState<string | null>(null)

  const { replace } = useRouter()
  const formMethods = useForm(VALIDATOR)
  const email = formMethods.watch(FORM_FIELD_NAME.email)

  useEffect(() => {
    sessionStorage.removeItem('isEligibleForDeferredPayments')
  }, [])

  const onSubmit: SubmitHandler<{ formData: IFormModel; mailData: string }> = useCallbackOnce(
    async ({ formData, mailData }) => {
      try {
        const { broker, accessToken } = await nest.auth.loginIn({
          email: formData.email,
          password: formData.password,
          isRememberMe: formData.remember,
        })

        replace(routes.partnerPortalDashboard)

        if (!formData.remember) {
          const jwtData = parseJwt(accessToken)
          const date = dateFormatter(unixToDate(jwtData.exp), {
            hour: '2-digit',
            minute: '2-digit',
          })
          toast.loading('Redirect to dashboard', {
            autoClose: false,
            toastId: REDIRECT_LOGIN_TOAST_ID,
          })

          toast.success(
            () => (
              <p>
                Hello, {broker.firstName}! You did not check the &quot;Remember me&quot; box when
                you signed in. Your session will end at {date}.
              </p>
            ),
            {
              autoClose: 15000,
            },
          )
          return
        }
        toast.loading('Redirect to dashboard', {
          autoClose: false,
          toastId: REDIRECT_LOGIN_TOAST_ID,
        })
        toast.success(`Hello, ${broker.firstName}!`)
      } catch (err) {
        let errorText = String(err)

        if (axios.isAxiosError(err) && err.response?.data?.message) {
          errorText = err.response.data.message

          if (err.response?.data?.field === 'isValidated' && err.response?.data?.uuid) {
            try {
              toast.info(
                'Please finalize registration process by confirming your email. New verification code has just been sent to ' +
                  mailData,
                {
                  autoClose: false,
                  toastId: REDIRECT_LOGIN_TOAST_ID,
                },
              )
              await nest.auth.resendBrokerValidationCode(mailData)

              setBrokerUuid(err.response.data.uuid)
              setShowVerifyEmailDialog(true)
            } catch (e) {
              errorText = e

              if (axios.isAxiosError(e) && e.response?.data?.message) {
                errorText = e.response.data.message
              }
            }

            toast.error('Error while resending broker validation code: ' + errorText, {
              autoClose: false,
              toastId: REDIRECT_LOGIN_TOAST_ID,
            })

            return
          }
        }

        toast.error('Error while signing in: ' + errorText, {
          autoClose: false,
          toastId: REDIRECT_LOGIN_TOAST_ID,
        })
      }
    },
  )

  useUnmount(() => {
    toast.dismiss(REDIRECT_LOGIN_TOAST_ID)
  })

  return (
    <>
      <NextSeo title="Partner Portal" />
      <AuthPartnerPortalLayout>
        <AutHeader title="Partner sign in" />
        <p>Login to manage your REZITRADE Partner account.</p>
        <Form
          formMethods={formMethods}
          onSubmit={(data) => onSubmit({ formData: data, mailData: email })}
          className={classes.form}
        >
          <EmailFormField
            name={FORM_FIELD_NAME.email}
            placeholder="Enter your email address"
            label={null}
            fullWidth
            className={classes.emailField}
          />
          <PasswordFormField
            autoComplete="current-password"
            label={null}
            name={FORM_FIELD_NAME.password}
            placeholder="Enter your password"
            fullWidth
            className={classes.password}
          />

          <CheckboxFormField name={FORM_FIELD_NAME.remember} className={classes.remember}>
            Remember me
          </CheckboxFormField>
          <Link
            href={routes.partnerPortalForgotPassword}
            className={cn(classes.forgotPassword)}
            theme="primaryLight"
          >
            Forgot password?
          </Link>
          <SubmitFormButton theme="primary" className={classes.submit}>
            Login
          </SubmitFormButton>
        </Form>

        <RegistrationLink />

        {brokerUuid && (
          <VerifyEmailDialog
            verificationFor={verificationForEnum.broker}
            className={classes.dialog}
            email={email}
            uuid={brokerUuid}
            open={showVerifyEmailDialog}
            onClose={() => setShowVerifyEmailDialog(false)}
            onSuccess={async () => {
              toast.success('You have successfully confirmed your email!')
              setShowVerifyEmailDialog(false)
            }}
          />
        )}
      </AuthPartnerPortalLayout>
    </>
  )
}
